import { gql, useLazyQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import SlideoutMenu from '@orn/shared-web/components/SlideoutMenu';
import { CalendarIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useFeatureGate } from '../../../hooks/useFeatureGates';
import { PATHS } from '../../../paths';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { formatCalendarDate } from '../../../utils/formatting';
import { getAverageAmountLabel } from '../../../utils/recurring';
import Entity from '../../entity';
import { SlideoutAmountDisplay, SlideoutEntityHeader, SlideoutHeaderCaption, SlideoutTimingTag } from '../header';
import {
  AccountRow,
  IsRecurringRow,
  JsonDebugRow,
  LastPaymentRow,
  NameRow,
  RecurringFrequencyRow,
  RecurringTypeRow,
  YearlySpendRow,
} from '../rows';
import TransactionSlideout from '../TransactionSlideout';

import type {
  GetRecurringTransactionForSlideoutQuery,
  GetRecurringTransactionForSlideoutQueryVariables,
} from './__generated__/RecurringTransactionSlideout.gql';

export type RecurringTransaction = GetRecurringTransactionForSlideoutQuery['getRecurringTransaction'];

const GET_RECURRING_TRANSACTION_FOR_SLIDEOUT = gql`
  query GetRecurringTransactionForSlideout($id: ID!) {
    getRecurringTransaction(id: $id) {
      id
      description
      type
      frequency
      nextOccurrence
      amountRange
      rawJson
      lastTransaction {
        id
        date
        amount
        financialAccount {
          id
          name
          mask
          ...EntityAvatar
        }
      }
      merchant {
        id
        ...EntityAvatar
      }
      financialAccount {
        id
        name
        mask
        subtype
        ...EntityAvatar
        bank {
          id
          name
        }
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

interface IBaseRecurringTransactionSlideoutProps {
  isOpen?: boolean;
  /** Whether to render the `JsonDebugRow`. Wired by the wrapper from `FeatureGate.DebugTransactionDetails`. */
  showDebugJson?: boolean;
  onClickFinancialAccount: () => void;
  onClickMerchant?: () => void;
  onNavigateToTransaction: (transactionId: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
  recurringTransaction: RecurringTransaction | null;
}

export const BaseRecurringTransactionSlideout = ({
  isOpen,
  showDebugJson = false,
  onClickFinancialAccount,
  onClickMerchant,
  onNavigateToTransaction,
  onOpenChange,
  recurringTransaction,
}: IBaseRecurringTransactionSlideoutProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <SlideoutMenu isOpen={isOpen} onOpenChange={onOpenChange} ariaLabel="Recurring transaction details">
      {({ close }) =>
        !recurringTransaction ? (
          <SlideoutMenu.Content>
            <div className="py-50 flex items-center justify-center">
              <LoadingIndicator size="lg" />
            </div>
          </SlideoutMenu.Content>
        ) : (
          <>
            <SlideoutMenu.Header
              onClose={close}
              leftContent={
                recurringTransaction.nextOccurrence ? (
                  <SlideoutTimingTag date={recurringTransaction.nextOccurrence} variant="recurring" />
                ) : undefined
              }
            >
              <div className="flex w-full flex-col items-center gap-4">
                <SlideoutEntityHeader
                  entity={recurringTransaction.merchant ?? null}
                  name={recurringTransaction.merchant?.name ?? recurringTransaction.description}
                  onClick={recurringTransaction.merchant?.id ? onClickMerchant : undefined}
                />
                <div className="flex w-full flex-col items-center justify-center">
                  <SlideoutAmountDisplay
                    value={getAverageAmountLabel(recurringTransaction.amountRange, { abbreviated: false })}
                  />
                  <SlideoutHeaderCaption icon={<CalendarIcon className="size-4.5 text-utility-brand-300 shrink-0" />}>
                    {recurringTransaction.nextOccurrence
                      ? `Expected ${formatCalendarDate(recurringTransaction.nextOccurrence, 'medium')}`
                      : 'N/A'}
                  </SlideoutHeaderCaption>
                </div>
              </div>
            </SlideoutMenu.Header>
            <SlideoutMenu.Content>
              <div className="flex flex-col gap-3">
                <SlideoutMenu.Container className="overflow-y-auto">
                  <AccountRow
                    financialAccount={recurringTransaction.financialAccount}
                    onClick={onClickFinancialAccount}
                    caption="Expected"
                  />
                </SlideoutMenu.Container>
                <SlideoutMenu.Container className="overflow-y-auto">
                  <IsRecurringRow />
                  <NameRow name={recurringTransaction.description} />
                  <RecurringTypeRow type={recurringTransaction.type} />
                  <RecurringFrequencyRow frequency={recurringTransaction.frequency} />
                  <LastPaymentRow
                    lastTransaction={
                      recurringTransaction.lastTransaction?.financialAccount
                        ? {
                            ...recurringTransaction.lastTransaction,
                            financialAccount: recurringTransaction.lastTransaction.financialAccount,
                          }
                        : null
                    }
                    onClick={onNavigateToTransaction}
                  />
                  <YearlySpendRow
                    amountRange={recurringTransaction.amountRange}
                    frequency={recurringTransaction.frequency}
                  />
                  {showDebugJson && (
                    <JsonDebugRow label="Recurring transaction JSON" rawJson={recurringTransaction.rawJson} />
                  )}
                </SlideoutMenu.Container>
              </div>
            </SlideoutMenu.Content>
          </>
        )
      }
    </SlideoutMenu>
  );
};

interface IRecurringTransactionSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  recurringTransactionId: string | null;
}

const RecurringTransactionSlideout = ({
  isOpen,
  onClose,
  recurringTransactionId,
}: IRecurringTransactionSlideoutProps) => {
  const navigate = useNavigateWithDateRange();
  const showDebugJson = useFeatureGate(FeatureGate.DebugTransactionDetails);
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);
  const [fetchRecurringTransaction, { data, error }] = useLazyQuery<
    GetRecurringTransactionForSlideoutQuery,
    GetRecurringTransactionForSlideoutQueryVariables
  >(GET_RECURRING_TRANSACTION_FOR_SLIDEOUT);

  React.useEffect(() => {
    if (isOpen && recurringTransactionId) {
      void fetchRecurringTransaction({ variables: { id: recurringTransactionId } });
    }
  }, [isOpen, recurringTransactionId, fetchRecurringTransaction]);

  React.useEffect(() => {
    if (error) {
      onClose();
    }
  }, [error, onClose]);

  const recurringTransaction = React.useMemo((): RecurringTransaction | null => {
    if (!recurringTransactionId) {
      return null;
    }
    const fetched = data?.getRecurringTransaction;
    if (!fetched || fetched.id !== recurringTransactionId) {
      return null;
    }
    return fetched;
  }, [data?.getRecurringTransaction, recurringTransactionId]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  const onClickMerchant = React.useCallback(() => {
    if (recurringTransaction?.merchant?.id) {
      onClose();
      void navigate(PATHS.MERCHANT(recurringTransaction.merchant.id));
    }
  }, [recurringTransaction?.merchant?.id, navigate, onClose]);

  const onClickFinancialAccount = React.useCallback(() => {
    if (recurringTransaction?.financialAccount?.id) {
      onClose();
      void navigate(PATHS.ACCOUNT(recurringTransaction.financialAccount.id));
    }
  }, [recurringTransaction?.financialAccount?.id, navigate, onClose]);

  const handleNavigateToTransaction = React.useCallback(
    (transactionId: string) => {
      onClose();
      setSelectedTransactionId(transactionId);
    },
    [onClose]
  );

  return (
    <>
      <BaseRecurringTransactionSlideout
        isOpen={isOpen}
        showDebugJson={showDebugJson}
        onClickFinancialAccount={onClickFinancialAccount}
        onClickMerchant={recurringTransaction?.merchant?.id ? onClickMerchant : undefined}
        onNavigateToTransaction={handleNavigateToTransaction}
        onOpenChange={handleOpenChange}
        recurringTransaction={recurringTransaction}
      />
      <TransactionSlideout
        transactionId={selectedTransactionId}
        isOpen={selectedTransactionId !== null}
        onClose={() => setSelectedTransactionId(null)}
      />
    </>
  );
};

export default RecurringTransactionSlideout;
